import {
  HubConnectionBuilder,
  HttpTransportType
} from '@aspnet/signalr';
import axios from 'axios';
import moment from 'moment';

import uuidv1 from 'uuid/v1';
import Vue from 'vue';

import appSettings from './appSettings';

export default {
  async addSignalR(app, scope) {
    app.connection = new HubConnectionBuilder()
      .withUrl('/bedBoardHub', {
        accessTokenFactory: () => {
          const loginParameters = app.$sdkAuth.getLoginParameters('session');
          if (loginParameters && loginParameters.ntstOAuthTokenData) {
            return loginParameters.ntstOAuthTokenData.accessToken;
          }
          return null;
        },
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets
      })
      .build();
    app.connection.onclose(async () => {
      app.$log.debug(`[${moment().format(appSettings.logDateFormat)}] signalR connection closed`);
      await this.startSignalRConnection(app, scope);
    });
    app.connection.on('AssignClient', (data) => {
      app.$log.debug(`[${moment().format(appSettings.logDateFormat)}] 'AssignClient' signalr message received.`);
      app.$store.commit('assignStay', data);
    });
    app.connection.on('BedBoardStayUpdated', (data) => {
      app.$log.debug(`[${moment().format(appSettings.logDateFormat)}] 'BedBoardStayUpdated' signalr message received.`);
      app.$store.commit('updateStay', data);
    });
    app.connection.on('ClientImageCreated', (data) => {
      app.$log.debug(`[${moment().format(appSettings.logDateFormat)}] 'ClientImageCreated' signalr message received.`);
      app.$store.commit('updateClientImage', data);
    });
    app.connection.on('ClientImageDeleted', (data) => {
      app.$log.debug(`[${moment().format(appSettings.logDateFormat)}] 'ClientImageDeleted' signalr message received.`);
      app.$store.commit('deleteClientImage', data);
    });
    app.connection.on('ClientImageUpdated', (data) => {
      app.$log.debug(`[${moment().format(appSettings.logDateFormat)}] 'ClientImageUpdated' signalr message received.`);
      app.$store.commit('updateClientImage', data);
    });
    app.connection.on('MoveStay', (data) => {
      app.$log.debug(`[${moment().format(appSettings.logDateFormat)}] 'MoveStay' signalr message received.`);
      app.$store.commit('moveStay', data);
    });
    app.connection.on('RemoveStay', (data) => {
      app.$log.debug(`[${moment().format(appSettings.logDateFormat)}] 'RemoveStay' signalr message received.`);
      app.$store.commit('removeStay', data);
    });
    app.connection.on('SwapStays', (data) => {
      app.$log.debug(`[${moment().format(appSettings.logDateFormat)}] 'SwapStays' signalr message received.`);
      app.$store.commit('swapStays', data);
    });
    app.connection.on('UpdateRoomValues', (data) => {
      app.$log.debug(`[${moment().format(appSettings.logDateFormat)}] 'UpdateRoomValues' signalr message received.`);
      app.$store.commit('updateRoomValues', data);
    });
    app.connection.on('UpdateStayValues', (data) => {
      app.$log.debug(`[${moment().format(appSettings.logDateFormat)}] 'UpdateStayValues' signalr message received.`);
      app.$store.commit('updateStayValues', data);
    });

    await this.startSignalRConnection(app, scope);
  },

  async authHeader() {
    // Return authorization header with jwt token.
    const loginParameters = await Vue.prototype.$sdkAuth.getLoginParameters('session');
    if (loginParameters && loginParameters.ntstOAuthTokenData && loginParameters.ntstOAuthTokenData.accessToken && loginParameters.ntstOAuthTokenData.tokenType) {
      return {
        Authorization: `Bearer ${loginParameters.ntstOAuthTokenData.accessToken}`
      };
    }
    return {};
  },

  buildBedBoardStayTransaction(params) {
    let returnValue = null;
    if (params && params.stayID && params.stayID.id && params.clientID && params.clientID.id) {
      returnValue = {
        auditInformation: {
          createdByStaffMemberID: params.staffMemberProviderID,
          createdDate: moment().format(),
          lastUpdatedByStaffMemberID: params.staffMemberProviderID,
          lastUpdatedDate: moment().format()
        },
        bedBoardStayID: params.stayID,
        bedBoardStayTransactionID: {
          scopeID: params.stayID.scopeID,
          id: uuidv1()
        },
        clientID: params.clientID,
        transactionDate: moment().format(),
        transactionNarrative: params.narrative
      };
    }
    return returnValue;
  },

  buildRoomAssignment(params) {
    let returnValue = null;
    if (params && params.roomID && params.roomID.id && params.unitID && params.unitID.id) {
      returnValue = {
        bedBoardBedID: params.bedID,
        bedBoardRoomID: params.roomID,
        bedboardUnitID: params.unitID
      };
    }
    return returnValue;
  },

  clone(fromObject) {
    let returnValue = null;
    if (fromObject) {
      const json = JSON.stringify(fromObject);
      returnValue = JSON.parse(json);
    }
    return returnValue;
  },

  executeConfig(params) {
    return new Promise(async (resolve, reject) => {
      if (params && params.scope && params.providerID) {
        const inputPayload = {
          extendedAttributes: [{
            type: 'UserName',
            value: params.userName
          }],
          providerID: {
            scopeID: params.scope,
            id: params.providerID
          }
        };

        await axios.post('/data/config', inputPayload, {
          headers: await this.authHeader(),
        }).catch(error => reject(error))
          .then((response) => {
            if (response && response.data) {
              resolve(response.data);
            } else {
              reject(response);
            }
          });
      } else {
        reject(new Error('bedBoardUtils.executeConfig: Required parameter values not provided.'));
      }
    });
  },

  executeGenerateBedBoardOccupancyReport(providerID) {
    return new Promise(async (resolve, reject) => {
      if (providerID) {
        try {
          const response = await axios.post('/data/occupancyReport', providerID, {
            headers: await this.authHeader(),
          });
          if (response && response.data) {
            resolve(response.data);
          } else {
            reject(response);
          }
        } catch (error) {
          reject(new Error(error.response));
        }
      } else {
        reject(new Error('no params'));
      }
    });
  },

  executeGetClientImage(params) {
    return new Promise(async (resolve, reject) => {
      if (params && params.scopeID && params.clientID) {
        const inputPayload = {
          requestedClientID: {
            scopeID: params.scopeID,
            id: params.clientID
          }
        };
        try {
          const response = await axios.post('/data/clientImage', inputPayload, {
            headers: await this.authHeader(),
          });
          if (response && response.data) {
            resolve(response.data);
          } else {
            reject(response);
          }
        } catch (error) {
          reject(new Error(error.response));
        }
      } else {
        reject(new Error('no params'));
      }
    });
  },

  executeLogin(loginParameters) {
    return new Promise(async (resolve, reject) => {
      if (loginParameters && loginParameters.scope && loginParameters.userName && loginParameters.password && loginParameters.systemID) {
        const inputPayload = {
          credentials: {
            userID: loginParameters.userName,
            password: loginParameters.password
          },
          systemID: {
            scopeID: loginParameters.scope,
            id: loginParameters.systemID
          }
        };

        try {
          const response = await axios.post('/security/login', inputPayload);
          if (response && response.data) {
            resolve(response.data);
          } else {
            reject(new Error('Execute login did not return response data.'));
          }
        } catch (error) {
          reject(new Error(error.response));
        }
      } else {
        reject(new Error('no params'));
      }
    });
  },

  executeRefreshToken() {
    return new Promise(async (resolve, reject) => {
      const loginParameters = await Vue.prototype.$sdkAuth.getLoginParameters('session');
      if (loginParameters && loginParameters.ntstOAuthTokenData && loginParameters.ntstOAuthTokenData.refreshToken) {
        const inputPayload = {
          authorizationType: loginParameters.authorizationType,
          ntstOAuthTokenData: loginParameters.ntstOAuthTokenData
        };

        await axios.post('/security/refTok', inputPayload, {
          headers: await this.authHeader(),
        })
          .then((response) => {
            if (response && response.data) {
              resolve(response.data);
            } else {
              reject(new Error('Refresh Token call missing response data.'));
            }
          })
          .catch((err) => {
            if (err && err.message) {
              reject(new Error(`bedBoardUtils.executeRefreshToken: ${err.message}`));
            } else {
              reject(new Error('bedBoardUtils.executeRefreshToken: Failed for unknown reason.'));
            }
          });
      } else {
        this.$log.error(`[${moment().format(appSettings.logDateFormat)}] executeRefreshToken: No LoginParameters data provided.`);
        reject(new Error('no login params for refresh'));
      }
    });
  },

  executeSearchBedBoardClient(params) {
    return new Promise(async (resolve, reject) => {
      if (params) {
        const inputPayload = {
          assigningToBedBoardRoomID: params.assigningToBedBoardRoomID,
          clientFirstName: params.clientFirstName,
          clientLastName: params.clientLastName,
          extendedAttributes: [{
            type: 'UserName',
            value: params.userName
          }],
          humanReadableClientID: params.humanReadableClientID,
          isSearchAllocatedOnly: params.isSearchAllocatedOnly,
          isSearchAllocatedToRoomOnly: params.isSearchAllocatedToRoomOnly,
        };
        try {
          const response = await axios.post('/data/searchBedBoardClient', inputPayload, {
            headers: await this.authHeader(),
          });
          if (response && response.data) {
            resolve(response.data);
          } else {
            reject(response);
          }
        } catch (error) {
          reject(new Error(error.response));
        }
      } else {
        reject(new Error('no params'));
      }
    });
  },

  getProviderNameForStaffMember(staffMember) {
    let returnValue = null;

    if (staffMember && staffMember.name) {
      returnValue = `${staffMember.name.first} ${staffMember.name.last}`;
    }
    return returnValue;
  },

  getStaffMemberProviderIDForStaffMember(staffMember) {
    let returnValue = null;

    if (staffMember && staffMember.identifications) {
      const providerID = staffMember.identifications.find(obj => obj.type === 'ProviderID');
      if (providerID) {
        returnValue = providerID.value;
      } else if (staffMember && staffMember.associatedProviderID) {
        returnValue = staffMember.associatedProviderID.id;
      }
    }
    return returnValue;
  },

  generateGuid() {
    return uuidv1();
  },

  getNodesFromHierarchy(hierarchies, selectedUnitIDs) {
    const returnValue = [];

    if (hierarchies) {
      hierarchies.forEach((hierarchy) => {
        const newNode = {
          text: hierarchy.name,
          state: {
            expanded: true
          },
          children: []
        };

        if (hierarchy.childrenHierarchy && hierarchy.childrenHierarchy.length > 0) {
          newNode.children = this.getNodesFromHierarchy(hierarchy.childrenHierarchy, selectedUnitIDs);
        }


        if (hierarchy.units && hierarchy.units.length > 0) {
          hierarchy.units.forEach((unit) => {
            newNode.children.push({
              data: unit.bedBoardUnitID,
              text: unit.name,
              state: {
                checked: selectedUnitIDs.find(cfid => cfid.id === unit.bedBoardUnitID.id)
              }
            });
          });
        }
        returnValue.push(newNode);
      });
    }
    return returnValue;
  },

  getRoomFromHierarchyWithRoomID(hierarchy, roomID) {
    let returnValue = null;
    if (hierarchy && roomID && roomID.id) {
      if (hierarchy.units && hierarchy.units.length > 0) {
        hierarchy.units.forEach((unit) => {
          if (!returnValue) {
            if (unit.rooms && unit.rooms.length > 0) {
              unit.rooms.forEach((room) => {
                if (!returnValue) {
                  const foundRoom = room.bedBoardRoomID.id === roomID.id;
                  if (foundRoom) {
                    returnValue = room;
                  }
                }
              });
            }
          }
        });
      }
      if (!returnValue) {
        // process children
        if (hierarchy.childrenHierarchy) {
          hierarchy.childrenHierarchy.forEach((hier) => {
            if (!returnValue) {
              returnValue = this.getRoomFromHierarchyWithRoomID(hier, roomID);
            }
          });
        }
      }
    }
    return returnValue;
  },

  getRoomFromHierarchyWithStayID(hierarchy, stayID) {
    let returnValue = null;
    if (hierarchy && stayID && stayID.id) {
      if (hierarchy.units && hierarchy.units.length > 0) {
        hierarchy.units.forEach((unit) => {
          if (!returnValue) {
            if (unit.rooms && unit.rooms.length > 0) {
              unit.rooms.forEach((room) => {
                if (!returnValue) {
                  const foundStay = this.getStayFromRoom(room, stayID.id);
                  if (foundStay) {
                    returnValue = room;
                  }
                }
              });
            }
          }
        });
      }
      if (!returnValue) {
        // process children
        if (hierarchy.childrenHierarchy) {
          hierarchy.childrenHierarchy.forEach((hier) => {
            if (!returnValue) {
              returnValue = this.getRoomFromHierarchyWithStayID(hier, stayID);
            }
          });
        }
      }
    }
    return returnValue;
  },

  getRoomFromHierarchy(hierarchy, unitID, roomID) {
    let returnValue = null;
    const units = this.getUnitsFromHierarchy(hierarchy, [unitID]);
    if (units && units.length > 0) {
      returnValue = this.getRoomFromUnit(units[0], roomID);
    }

    return returnValue;
  },

  getRoomFromUnit(unit, roomID) {
    let returnValue = null;
    if (unit && unit.rooms) {
      returnValue = unit.rooms.find(room => room.bedBoardRoomID.id === roomID);
    }

    return returnValue;
  },

  getStayFromHierarchy(hierarchy, unitID, roomID, stayID) {
    let returnValue = null;
    const room = this.getRoomFromHierarchy(hierarchy, unitID, roomID);
    returnValue = this.getStayFromRoom(room, stayID);

    return returnValue;
  },

  getStayFromRoom(room, stayID) {
    let returnValue = null;
    if (room && room.stays && room.stays.length > 0) {
      const index = room.stays.findIndex(stay => stay.bedBoardStayID.id === stayID);
      if (index >= 0) {
        returnValue = room.stays[index];
      }
    }

    return returnValue;
  },

  getNtstOAuthTokenDataFromVerifySystemAccountOutputPayload(outputPayload) {
    let returnValue = null;

    if (outputPayload && outputPayload.extendedAttributes && outputPayload.extendedAttributes.length > 0) {
      outputPayload.extendedAttributes.forEach((extendedAttribute) => {
        if (extendedAttribute.id === 'LoginParameters' && extendedAttribute.value) {
          const loginParameters = JSON.parse(extendedAttribute.value);
          returnValue = loginParameters.ntstOAuthTokenData;
        }
      });
    }
    return returnValue;
  },

  getUnitsFromHierarchy(hierarchy, unitIDs) {
    const returnValue = [];
    if (unitIDs && unitIDs.length > 0 && hierarchy && hierarchy.units && hierarchy.units.length > 0) {
      const selectUnits = hierarchy.units.filter(unit => unitIDs.includes(unit.bedBoardUnitID.id));
      returnValue.push(...selectUnits);
    }
    // process children
    if (hierarchy && hierarchy.childrenHierarchy && hierarchy.childrenHierarchy.length > 0) {
      hierarchy.childrenHierarchy.forEach((hier) => {
        returnValue.push(...this.getUnitsFromHierarchy(hier, unitIDs));
      });
    }
    return returnValue;
  },

  initializeUnits(units) {
    if (units && units.length > 1) {
      units.forEach((unit) => {
        if (unit.rooms && unit.rooms.length > 0) {
          unit.rooms.forEach((room) => {
            if (room.stays && room.stays.length > 0) {
              room.stays.forEach((stay) => {
                if ((!stay.bedBoardAlerts)) {
                  stay.bedBoardAlerts = [];
                }
              });
            }
          });
        }
      });
    }
  },

  isRoomMaxOccupancyExceeded(room, store) {
    let returnValue = false;

    if (room && room.isMaximumOccupancyEnforced && room.stays) {
      const numberOfActionableStays = store.getters.getNumberOfActionableStays(room);
      returnValue = numberOfActionableStays >= room.maximumOccupancyCount;
    }
    return returnValue;
  },

  isUnitMaxOccupancyExceeded(unit) {
    let returnValue = false;

    if (unit && unit.isMaximumOccupancyEnforced && unit.rooms && unit.rooms.length > 0) {
      const filledCnt = unit.rooms.reduce((stayCnt, room) => {
        let val = stayCnt;
        if (room.stays) {
          val = stayCnt + room.stays.length;
        }
        return val;
      }, 0);
      returnValue = filledCnt >= unit.maximumOccupancyCount;
    }
    return returnValue;
  },

  raiseBedBoardRoomUpdated(room) {
    return new Promise(async (resolve, reject) => {
      if (room && room.bedBoardRoomID && room.bedBoardRoomID.scopeID && room.bedBoardRoomID.id) {
        try {
          const response = await axios.put('/data/room', room, {
            headers: await this.authHeader(),
          });
          if (response && response.data) {
            resolve(response.data);
          } else {
            reject(response);
          }
        } catch (error) {
          reject(new Error(error.response));
        }
      } else {
        reject(new Error('no room'));
      }
    });
  },

  raiseBedBoardStayUpdated(stay) {
    return new Promise(async (resolve, reject) => {
      if (stay && stay.bedBoardStayID && stay.bedBoardStayID.scopeID && stay.bedBoardStayID.id) {
        try {
          const response = await axios.put('/data/stay', stay, {
            headers: await this.authHeader(),
          });
          if (response && response.data) {
            resolve(response.data);
          } else {
            reject(response);
          }
        } catch (error) {
          reject(new Error(error.response));
        }
      } else {
        reject(new Error('no stay'));
      }
    });
  },

  raiseBedBoardUserPreferencesUpdated(preferences) {
    return new Promise(async (resolve, reject) => {
      if (preferences && preferences.providerID && preferences.providerID.scopeID && preferences.providerID.id) {
        try {
          const response = await axios.put('/data/userPrefs', preferences, {
            headers: await this.authHeader(),
          });
          if (response && response.data) {
            resolve(response.data);
          } else {
            reject(response);
          }
        } catch (error) {
          reject(new Error(error.response));
        }
      } else {
        reject(new Error('no preferences'));
      }
    });
  },

  raiseBedBoardStayAssignRoomTransactionCreated(params) {
    return new Promise(async (resolve, reject) => {
      if (params) {
        const transaction = this.buildBedBoardStayTransaction(params);

        if (transaction) {
          transaction.toRoomAssignment = this.buildRoomAssignment(params);
          if (transaction.toRoomAssignment) {
            try {
              const response = await axios.put('/data/assignStay', transaction, {
                headers: await this.authHeader(),
              });
              if (response && response.data) {
                resolve(response.data);
              } else {
                reject(response);
              }
            } catch (error) {
              reject(new Error(error.response));
            }
          } else {
            reject(new Error('missing params'));
          }
        } else {
          reject(new Error('missing params'));
        }
      } else {
        reject(new Error('no params'));
      }
    });
  },

  raiseBedBoardMoveStayTransactionCreated(params) {
    return new Promise(async (resolve, reject) => {
      if (params) {
        const transaction = this.buildBedBoardStayTransaction(params.fromValues);

        if (transaction) {
          transaction.fromRoomAssignment = this.buildRoomAssignment(params.fromValues);
          transaction.toRoomAssignment = this.buildRoomAssignment(params.toValues);
          if (transaction.fromRoomAssignment && transaction.toRoomAssignment) {
            try {
              const response = await axios.put('/data/moveStay', transaction, {
                headers: await this.authHeader(),
              });
              if (response && response.data) {
                resolve(response.data);
              } else {
                reject(response);
              }
            } catch (error) {
              reject(new Error(error.response));
            }
          } else {
            reject(new Error('missing params'));
          }
        } else {
          reject(new Error('missing params'));
        }
      } else {
        reject(new Error('no params'));
      }
    });
  },

  raiseBedBoardRemoveStayTransactionCreated(params) {
    return new Promise(async (resolve, reject) => {
      if (params) {
        const transaction = this.buildBedBoardStayTransaction(params);

        if (transaction) {
          transaction.currentRoomAssignment = this.buildRoomAssignment(params);
          if (transaction.currentRoomAssignment) {
            try {
              const response = await axios.put('/data/removeStay', transaction, {
                headers: await this.authHeader(),
              });
              if (response && response.data) {
                resolve(response.data);
              } else {
                reject(response);
              }
            } catch (error) {
              reject(new Error(error.response));
            }
          } else {
            reject(new Error('missing params'));
          }
        } else {
          reject(new Error('missing params'));
        }
      } else {
        reject(new Error('no params'));
      }
    });
  },

  raiseBedBoardSwapStayTransactionCreated(params) {
    return new Promise(async (resolve, reject) => {
      if (params) {
        const transaction = this.buildBedBoardStayTransaction(params.fromValues);

        if (transaction) {
          transaction.swapWithBedBoardStayID = params.withValues.stayID;
          transaction.swapWithClientID = params.withValues.clientID;

          transaction.fromRoomAssignment = this.buildRoomAssignment(params.fromValues);
          transaction.toRoomAssignment = this.buildRoomAssignment(params.withValues);

          if (transaction.fromRoomAssignment && transaction.toRoomAssignment) {
            try {
              const response = await axios.put('/data/swapStays', transaction, {
                headers: await this.authHeader(),
              });
              if (response && response.data) {
                resolve(response.data);
              } else {
                reject(response);
              }
            } catch (error) {
              reject(new Error(error.response));
            }
          } else {
            reject(new Error('missing params'));
          }
        } else {
          reject(new Error('missing params'));
        }
      } else {
        reject(new Error('no params'));
      }
    });
  },

  async removeClientFromSignalRGroup(app, scope) {
    if (app && app.connection && scope) {
      this.sendRemoveClientFromSignalRGroup(app.connection, scope);
      app.$log.debug(`[${moment().format(appSettings.logDateFormat)}] Client removed from signalR group: ${scope}`);
    }
  },

  async removeSignalR(app) {
    if (app && app.connection) {
      app.connection = null;
    }
  },

  sendAddClientToSignalRGroup(connection, groupName) {
    return new Promise(async (resolve, reject) => {
      if (connection && groupName) {
        try {
          await connection.invoke('AddToGroup', groupName)
            .then(() => {
              resolve();
            })
            .catch((error) => {
              reject(error);
            });
        } catch (error) {
          reject(new Error(error.response));
        }
      } else {
        reject(new Error('no connection or no params'));
      }
    });
  },

  sendAssignClientSignal(source, params) {
    return new Promise(async (resolve, reject) => {
      const connection = source.$store.getters.signalRConnection;
      const scope = source.$sdkAuth.getScope('session');
      if (connection && params) {
        try {
          await connection.invoke('AssignClient', scope, params, params.stay.clientSummary.clientID.id)
            .then(() => {
              resolve();
            })
            .catch((error) => {
              reject(error);
            });
        } catch (error) {
          reject(new Error(error.response));
        }
      } else {
        reject(new Error('no connection or no params'));
      }
    });
  },

  sendMoveStaySignal(source, params) {
    return new Promise(async (resolve, reject) => {
      const connection = source.$store.getters.signalRConnection;
      const scope = source.$sdkAuth.getScope('session');
      if (connection && params && scope) {
        try {
          await connection.invoke('MoveStay', scope, params, params.fromValues.stayID.id)
            .then(() => {
              resolve();
            })
            .catch((error) => {
              reject(error);
            });
        } catch (error) {
          reject(new Error(error.response));
        }
      } else {
        reject(new Error('no connection or no params'));
      }
    });
  },

  sendRemoveClientFromSignalRGroup(connection, groupName) {
    return new Promise(async (resolve, reject) => {
      if (connection && groupName) {
        try {
          await connection.invoke('RemoveFromGroup', groupName)
            .then(() => {
              resolve();
            })
            .catch((error) => {
              reject(error);
            });
        } catch (error) {
          reject(new Error(error.response));
        }
      } else {
        reject(new Error('no connection or no params'));
      }
    });
  },

  sendRemoveStaySignal(source, params) {
    return new Promise(async (resolve, reject) => {
      const connection = source.$store.getters.signalRConnection;
      const scope = source.$sdkAuth.getScope('session');
      if (connection && params) {
        try {
          await connection.invoke('RemoveStay', scope, params, params.stayID.id)
            .then(() => {
              resolve();
            })
            .catch((error) => {
              reject(error);
            });
        } catch (error) {
          reject(new Error(error.response));
        }
      } else {
        reject(new Error('no connection or no params'));
      }
    });
  },

  sendSwapStaysSignal(source, params) {
    return new Promise(async (resolve, reject) => {
      const connection = source.$store.getters.signalRConnection;
      const scope = source.$sdkAuth.getScope('session');
      if (connection && params) {
        try {
          await connection.invoke('SwapStays', scope, params, params.fromValues.stayID.id, params.withValues.stayID.id)
            .then(() => {
              resolve();
            })
            .catch((error) => {
              reject(error);
            });
        } catch (error) {
          reject(new Error(error.response));
        }
      } else {
        reject(new Error('no connection or no params'));
      }
    });
  },

  sendUpdateRoomValuesSignal(source, params) {
    return new Promise(async (resolve, reject) => {
      const connection = source.$store.getters.signalRConnection;
      const scope = source.$sdkAuth.getScope('session');
      if (connection && params) {
        try {
          await connection.invoke('UpdateRoomValues', scope, params, params.roomID.id)
            .then(() => {
              resolve();
            })
            .catch((error) => {
              reject(error);
            });
        } catch (error) {
          reject(new Error(error.response));
        }
      } else {
        reject(new Error('no connection or no params'));
      }
    });
  },

  sendUpdateStayValuesSignal(source, params) {
    return new Promise(async (resolve, reject) => {
      const connection = source.$store.getters.signalRConnection;
      const scope = source.$sdkAuth.getScope('session');
      if (connection && params) {
        try {
          await connection.invoke('UpdateStayValues', scope, params, params.stayID.id)
            .then(() => {
              resolve();
            })
            .catch((error) => {
              reject(error);
            });
        } catch (error) {
          reject(new Error(error.response));
        }
      } else {
        reject(new Error('no connection or no params'));
      }
    });
  },

  sortRooms(rooms) {
    if (rooms && rooms.length > 1) {
      rooms.sort((a, b) => {
        if (a.sortOrder === b.sortOrder) {
          const x = a.name.toLowerCase();
          const y = b.name.toLowerCase();
          let value = 0;
          if (x < y) {
            value = -1;
          } else if (x > y) {
            value = 1;
          }
          return value;
        }
        return a.sortOrder - b.sortOrder;
      });
    }
  },

  sortUnits(units) {
    if (units && units.length > 1) {
      units.sort((a, b) => {
        if (a.sortOrder === b.sortOrder) {
          const x = a.name.toLowerCase();
          const y = b.name.toLowerCase();
          let value = 0;
          if (x < y) {
            value = -1;
          } else if (x > y) {
            value = 1;
          }
          return value;
        }
        return a.sortOrder - b.sortOrder;
      });
    }
  },

  async startSignalRConnection(app, scope) {
    try {
      await app.connection.start();
      app.$log.debug(`[${moment().format(appSettings.logDateFormat)}] signalR started.`);
      this.sendAddClientToSignalRGroup(app.connection, scope)
        .then(() => {
          app.$log.debug(`[${moment().format(appSettings.logDateFormat)}] Client added to signalR group: ${scope}`);
        })
        .catch(() => {
          app.$log.error(`[${moment().format(appSettings.logDateFormat)}] Unable to add Client to signalR group: ${scope}`);
        });
      app.$store.commit('setSignalRConnection', app.connection);
    } catch (err) {
      app.$log.error(`[${moment().format(appSettings.logDateFormat)}] Error: signalR start: ${err.message}`);
      setTimeout(() => this.startSignalRConnection(app, scope), 5000);
    }
  },

};
